import { useCallback, useMemo, useRef, useState } from 'react';

import { iaRa } from 'lib/helpers/utility';
import { useBillingRenderingProviders } from 'lib/hooks/queries/billing/useBillingRenderingProviders';
import { useStates } from 'lib/hooks/queries/states/useStates';

import Tabs from 'components/shared/Tabs/Tabs';

import { renderInfo } from '../../lib/helpers';

import AddProviderButton from './components/AddProviderButton/AddProviderButton';
import AddProviderStep from './components/AddProviderButton/AddProviderStep';
import AddProviderFooter from './components/AddProviderFooter';
import { buildTabsData } from './lib/buildTabsData';
import { buildOppositeStateMap, buildProviderRows } from './lib/helpers';
import { useOpenStatesModal } from './lib/useOpenStatesModal';
import { useTransientHighlights } from './lib/useTransientHighlights';

const InOutNetworkProviders = ({ values, practiceId, setFieldValue, openModal }) => {
  const addProviderStepRef = useRef();
  const selectionCountRef = useRef();
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const [activeTab, setActiveTab] = useState(0);
  const { highlights: modifiedProviderIds, markHighlight } = useTransientHighlights();

  const { in_network_providers, out_of_network_providers } = values || {};

  const { data: billingAndRenderingProvidersData } = useBillingRenderingProviders({
    params: { practiceId }
  });

  const { data: statesData } = useStates();

  const stateOptions = useMemo(
    () =>
      iaRa(statesData?.states).map((s) => {
        return { value: s.id, label: s.name, abbreviation: s.abbreviation };
      }),
    [statesData]
  );

  const practitioners = useMemo(() => {
    const billingProviders = iaRa(billingAndRenderingProvidersData?.providers?.billing).map(
      (provider) => ({
        value: provider.id,
        label: provider.bill_name,
        type: 'billing',
        ...provider
      })
    );

    const renderingProviders = iaRa(billingAndRenderingProvidersData?.providers?.rendering).map(
      (provider) => ({
        value: provider.id,
        label: provider.fullName,
        type: 'rendering',
        ...provider
      })
    );

    return [...billingProviders, ...renderingProviders];
  }, [billingAndRenderingProvidersData]);

  const inNetworkProviders = useMemo(
    () => buildProviderRows(in_network_providers, practitioners),
    [in_network_providers, practitioners]
  );
  const outOfNetworkProviders = useMemo(
    () => buildProviderRows(out_of_network_providers, practitioners),
    [out_of_network_providers, practitioners]
  );

  const isInNetworkTab = activeTab === 0;

  const updateProviderStates = useCallback(
    (fieldName, providerId, nextStateIds) => {
      const current = iaRa(valuesRef.current?.[fieldName]);
      const idx = current.findIndex((e) => e.provider_id === providerId);
      if (idx === -1) return;

      const nextIds = iaRa(nextStateIds);
      const currentIds = iaRa(current[idx].state_ids);
      const unchanged =
        currentIds.length === nextIds.length && currentIds.every((id) => nextIds.includes(id));
      if (unchanged) return;

      const updated = [...current];
      updated[idx] = { ...current[idx], state_ids: nextIds };
      setFieldValue(fieldName, updated);
      markHighlight(fieldName, providerId);
    },
    [setFieldValue, markHighlight]
  );

  const handleRemoveProvider = useCallback(
    (fieldName, providerId) => {
      const current = iaRa(valuesRef.current?.[fieldName]);
      setFieldValue(
        fieldName,
        current.filter((entry) => entry.provider_id !== providerId)
      );
    },
    [setFieldValue]
  );

  const openAddProviderModal = useCallback(
    (forInNetworkTab) => {
      if (!openModal) return;

      const fieldName = forInNetworkTab ? 'in_network_providers' : 'out_of_network_providers';

      const handleAddProviders = (selectedProviderIds) => {
        const currentEntries = iaRa(valuesRef.current?.[fieldName]);
        const existingIds = new Set(currentEntries.map((e) => e.provider_id));
        const newEntries = selectedProviderIds
          .filter((id) => !existingIds.has(id))
          .map((id) => ({ provider_id: id, state_ids: [] }));

        setFieldValue(fieldName, [...currentEntries, ...newEntries]);
      };

      const sameNetworkProviderIds = iaRa(valuesRef.current?.[fieldName]).map((e) => e.provider_id);

      const AddProviderModalContent = ({ close }) => (
        <AddProviderStep
          ref={addProviderStepRef}
          onGoBack={close}
          practitioners={practitioners}
          onAdd={handleAddProviders}
          onSelectionCountChange={(count) => selectionCountRef.current?.(count)}
          isInNetwork={forInNetworkTab}
          selectedProviders={sameNetworkProviderIds}
        />
      );

      openModal({
        title: `Add ${forInNetworkTab ? 'in-network' : 'out-of-network'} providers`,
        content: AddProviderModalContent,
        onSubmit: () => addProviderStepRef.current?.handleAddProviders?.(),
        footer: ({ onClose }) => (
          <AddProviderFooter
            selectionCountRef={selectionCountRef}
            onClose={onClose}
            isInNetworkTab={forInNetworkTab}
            onSubmit={() => addProviderStepRef.current?.handleAddProviders?.()}
          />
        )
      });
    },
    [practitioners, setFieldValue, openModal]
  );

  const addProviderButton = (
    <AddProviderButton onOpenAddProvider={openAddProviderModal} isInNetworkTab={isInNetworkTab} />
  );

  const oppositeStateMap = useMemo(
    () => buildOppositeStateMap(in_network_providers, out_of_network_providers),
    [in_network_providers, out_of_network_providers]
  );

  const openStatesModal = useOpenStatesModal({
    openModal,
    stateOptions,
    oppositeStateMap,
    updateProviderStates
  });

  const tabsData = useMemo(
    () =>
      buildTabsData({
        isInNetworkTab,
        inNetworkProviders,
        outOfNetworkProviders,
        addProviderButton,
        stateOptions,
        handleRemoveProvider,
        openStatesModal,
        modifiedProviderIds
      }),
    [
      inNetworkProviders,
      outOfNetworkProviders,
      isInNetworkTab,
      addProviderButton,
      stateOptions,
      handleRemoveProvider,
      openStatesModal,
      modifiedProviderIds
    ]
  );

  return (
    <>
      <div className="col-span-3">
        <div className="mb-6 mt-2 h-[1px] w-full bg-neutral-200" />

        {renderInfo(
          'In-network & out of network providers',
          'Define which providers are in-network or out-of-network and the states each network applies to.'
        )}
      </div>

      <div className="col-span-3 overflow-hidden rounded-lg border border-solid border-neutral-200">
        <Tabs
          tabsData={tabsData}
          tabIndex={activeTab}
          setTabIndex={setActiveTab}
          hasContent
          theme="secondary"
          className="px-[10px] py-2"
          rightActions={addProviderButton}
        />
      </div>
    </>
  );
};

export default InOutNetworkProviders;
